/**
 * Where every 402 lands.
 *
 * The prices rendered here come from the server's catalog, never from a constant in this file. A
 * hardcoded price list is a promise the checkout does not have to keep: the buyer reads R$189, the
 * server charges whatever `billing/prices.ts` says, and the first anyone notices is a chargeback.
 * The tier names and what each includes are copy; the money is data.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type BillingState, type PlanOption } from "../api.js";
import { t } from "../i18n.js";

type Load =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; billing: BillingState };

/** Display order. `corporativo` is appended by the renderer — it has no self-serve price. */
const ORDER = ["facilitador", "equipe", "evento"] as const;

export function Upgrade(): React.JSX.Element {
  const [load, setLoad] = useState<Load>({ state: "loading" });
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.billing().then(({ data }) => {
      if (cancelled) return;
      setLoad(data ? { state: "ready", billing: data } : { state: "error" });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const buy = async (plan: string, option: PlanOption): Promise<void> => {
    const key = `${plan}:${option.billing}`;
    setPending(key);
    setFailed(null);
    // MP needs a payer email. It is a prefill on their screen, not an identity — entitlement is
    // bound to the row the server created under this session — so asking here is enough.
    const email = window.prompt(t("upgrade.emailPrompt")) ?? "";
    if (!email.includes("@")) {
      setPending(null);
      if (email !== "") setFailed(t("upgrade.emailInvalid"));
      return;
    }
    const { data } = await api.checkout(plan, option.billing, email);
    if (data.checkoutUrl) {
      // A full navigation, not a new tab: MP's flow ends by returning the payer to our backUrl,
      // and a popup would strand that return in a window the buyer has already dismissed.
      window.location.href = data.checkoutUrl;
      return;
    }
    setPending(null);
    setFailed(
      data.error === "BILLING_UNAVAILABLE" ? t("upgrade.unavailable") : t("upgrade.checkoutFailed"),
    );
  };

  if (load.state === "loading") return <Shell>{t("upgrade.loading")}</Shell>;
  if (load.state === "error") return <Shell>{t("upgrade.unavailable")}</Shell>;

  const { billing } = load;
  return (
    <Shell>
      <header className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-display-md">{t("upgrade.title")}</h1>
          <p className="mt-1 text-sm opacity-70">
            {t("upgrade.currentPlan")}: <strong>{t(`plan.${billing.plan}`)}</strong>
          </p>
        </div>
        <Link to="/host" className="text-sm underline">
          {t("upgrade.back")}
        </Link>
      </header>

      <SubscriptionNotice billing={billing} />

      {failed && (
        <p role="alert" className="mb-6 rounded-lg border border-warning p-4 text-sm font-bold text-warning">
          {failed}
        </p>
      )}

      <ul className="grid gap-4 md:grid-cols-2">
        {ORDER.map((plan) => {
          const entry = billing.billable.find((b) => b.plan === plan);
          if (!entry) return null;
          return (
            <PlanCard
              key={plan}
              plan={plan}
              options={entry.options}
              current={billing.plan === plan}
              pending={pending}
              onBuy={buy}
            />
          );
        })}
        {/* Published floor, contract-sold. Rendering it with a "from" price and a mailto is the
            honest shape — a Buy button here would take money for something not yet configured. */}
        <li className="rounded-lg border border-muted p-5">
          <h2 className="font-display text-display-xs">{t("plan.corporativo")}</h2>
          <p className="mt-2 text-sm opacity-80">{t("upgrade.corporativoBlurb")}</p>
          <a className="mt-4 inline-block text-sm underline" href="mailto:vendas@klapzo.app">
            {t("upgrade.talkToSales")}
          </a>
        </li>
      </ul>

      <p className="mt-8 text-xs opacity-60">{t("upgrade.taxNote")}</p>
    </Shell>
  );
}

/**
 * The gap between `plan` and `subscription` is the whole reason both are fetched.
 *
 * A host whose card failed sees `past_due` here while their entitlement has already dropped to
 * free — without this they would read "current plan: free" and conclude their purchase vanished.
 */
function SubscriptionNotice({ billing }: { billing: BillingState }): React.JSX.Element | null {
  const sub = billing.subscription;
  if (!sub) return null;
  if (sub.status === "past_due") {
    return (
      <p role="status" className="mb-6 rounded-lg border border-warning p-4 text-sm">
        <strong>{t("upgrade.pastDue")}</strong>{" "}
        <span className="opacity-80">{t("upgrade.pastDueHint", { plan: t(`plan.${sub.plan}`) })}</span>
      </p>
    );
  }
  if (sub.status === "active" && sub.expiresAt !== null) {
    return (
      <p role="status" className="mb-6 rounded-lg border border-muted p-4 text-sm">
        {t("upgrade.passActive", {
          plan: t(`plan.${sub.plan}`),
          date: new Date(sub.expiresAt * 1000).toLocaleDateString(),
        })}
      </p>
    );
  }
  return null;
}

function PlanCard({
  plan,
  options,
  current,
  pending,
  onBuy,
}: {
  plan: string;
  options: PlanOption[];
  current: boolean;
  pending: string | null;
  onBuy: (plan: string, o: PlanOption) => void | Promise<void>;
}): React.JSX.Element {
  return (
    <li className={`rounded-lg border p-5 ${current ? "border-accent" : "border-muted"}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-display text-display-xs">{t(`plan.${plan}`)}</h2>
        {current && <span className="text-xs font-bold uppercase text-accent">{t("upgrade.current")}</span>}
      </div>
      <p className="mt-2 text-sm opacity-80">{t(`upgrade.blurb.${plan}`)}</p>
      <div className="mt-4 space-y-2">
        {options.map((o) => {
          const key = `${plan}:${o.billing}`;
          return (
            <button
              key={key}
              type="button"
              disabled={pending !== null}
              onClick={() => void onBuy(plan, o)}
              className="w-full rounded-md bg-accent px-4 py-2 text-left font-bold text-surface disabled:opacity-50"
            >
              {pending === key ? t("upgrade.redirecting") : priceLabel(o)}
            </button>
          );
        })}
      </div>
    </li>
  );
}

/**
 * `Intl.NumberFormat` rather than a template string: R$ 2.268,00 is the Brazilian grouping and
 * `2268` read as "R$2268" invites a misread as R$22.68 at a glance.
 */
export function priceLabel(o: PlanOption): string {
  const money = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
  }).format(o.amount);
  if (o.billing === "one_off") return t("upgrade.buyOneOff", { money, days: String(o.durationDays ?? 0) });
  return t(o.billing === "annual" ? "upgrade.buyAnnual" : "upgrade.buyMonthly", { money });
}

function Shell({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <main className="mx-auto max-w-3xl px-4 py-10">{children}</main>;
}
